import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, ExternalLink, FileUp, Loader2, Paperclip, Plus, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const usd = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const API = "/field-reports/cost-report";
export const receiptHref = (url) => (url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : "");
export const reimbTotal = (items) => items.reduce((a, i) => a + (Number(i.amount) || 0), 0);
const toBody = (i) => ({ date: i.date, note: i.note || "", amount: Math.max(0, parseFloat(i.amount) || 0), receipt_url: i.receipt_url || "", receipt_name: i.receipt_name || "", receipt_type: i.receipt_type || "" });

const isImage = (item) => (item.receipt_type || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(item.receipt_url || "");

// Full-size receipt viewer that opens over the report
const ReceiptZoom = ({ item, open, onOpenChange, testId }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl p-3 sm:p-4" data-testid={`${testId}-zoom`}>
      <DialogHeader><DialogTitle className="text-sm truncate pr-6">{item.note || "Receipt"} · {item.receipt_name || "Receipt"}</DialogTitle></DialogHeader>
      <div className="bg-zinc-900 rounded flex items-center justify-center max-h-[75vh] overflow-auto">
        <img src={receiptHref(item.receipt_url)} alt={item.receipt_name || "Receipt"} className="max-h-[75vh] w-auto object-contain" data-testid={`${testId}-zoom-img`} />
      </div>
      <div className="flex justify-end">
        <a href={receiptHref(item.receipt_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 underline" data-testid={`${testId}-zoom-newtab`}><ExternalLink className="w-3.5 h-3.5" />Open in new tab</a>
      </div>
    </DialogContent>
  </Dialog>
);

// Receipt link: photos show a thumbnail that zooms in place; PDFs open in a new tab
const ReceiptLink = ({ item, testId }) => {
  const [zoom, setZoom] = useState(false);
  if (!isImage(item)) {
    return (
      <a href={receiptHref(item.receipt_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 underline max-w-[200px]" data-testid={testId}>
        <Paperclip className="w-3 h-3 shrink-0" /><span className="truncate">{item.receipt_name || "Receipt"}</span>
      </a>
    );
  }
  return (
    <>
      <button type="button" onClick={() => setZoom(true)} className="inline-flex items-center gap-1.5 text-blue-700 underline max-w-[200px] group text-left" title="Tap to enlarge" data-testid={testId}>
        <img src={receiptHref(item.receipt_url)} alt={item.receipt_name || "Receipt"} loading="lazy" className="w-10 h-10 shrink-0 rounded border border-zinc-300 object-cover bg-zinc-100 transition-transform group-hover:scale-105" data-testid={`${testId}-thumb`} />
        <span className="truncate">{item.receipt_name || "Receipt"}</span>
      </button>
      <ReceiptZoom item={item} open={zoom} onOpenChange={setZoom} testId={testId} />
    </>
  );
};

const ReimbRow = ({ item, i, onChange, onSave, onRemove }) => {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!(file.type.startsWith("image/") || file.type === "application/pdf")) return toast.error("Receipts must be an image or PDF");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/receipts`, fd);
      onSave({ ...item, receipt_url: data.url, receipt_name: data.name, receipt_type: data.content_type });
      toast.success("Receipt attached");
    } catch { toast.error("Receipt upload failed"); }
    finally { setUploading(false); }
  };
  const pick = (e) => { upload(e.target.files?.[0]); e.target.value = ""; };
  return (
    <tr className="bg-white" data-testid={`reimb-row-${i}`}>
      <td className="border border-zinc-400 p-1"><Input type="date" value={item.date} onChange={(e) => onSave({ ...item, date: e.target.value })} className="h-8 w-36" data-testid={`reimb-date-${i}`} /></td>
      <td className="border border-zinc-400 p-1"><Input value={item.note} placeholder="What is it for? e.g. Hotel, fuel, parking" onChange={(e) => onChange({ ...item, note: e.target.value })} onBlur={() => onSave(item)} className="h-8 min-w-[200px]" data-testid={`reimb-note-${i}`} /></td>
      <td className="border border-zinc-400 p-1">
        <div className="relative"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
          <Input type="number" min="0" step="0.01" inputMode="decimal" value={item.amount} placeholder="0.00" onChange={(e) => onChange({ ...item, amount: e.target.value })} onBlur={() => onSave(item)} className="h-8 w-28 pl-5 text-right" data-testid={`reimb-amount-${i}`} />
        </div>
      </td>
      <td className="border border-zinc-400 p-1">
        <div className="flex items-center gap-1">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin text-zinc-500" data-testid={`reimb-uploading-${i}`} /> : item.receipt_url ? (
            <>
              <ReceiptLink item={item} testId={`reimb-receipt-link-${i}`} />
              <button type="button" onClick={() => onSave({ ...item, receipt_url: "", receipt_name: "", receipt_type: "" })} className="p-1 text-zinc-500 hover:text-red-600" title="Remove receipt" data-testid={`reimb-receipt-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs px-2" onClick={() => fileRef.current?.click()} data-testid={`reimb-upload-btn-${i}`}><FileUp className="w-3.5 h-3.5 mr-1" />Upload</Button>
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs px-2" onClick={() => camRef.current?.click()} data-testid={`reimb-camera-btn-${i}`}><Camera className="w-3.5 h-3.5 mr-1" />Photo</Button>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid={`reimb-file-input-${i}`} />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`reimb-camera-input-${i}`} />
        </div>
      </td>
      <td className="border border-zinc-400 p-1 text-center"><button type="button" onClick={onRemove} className="p-1.5 text-zinc-500 hover:text-red-600" title="Remove item" data-testid={`reimb-remove-${i}`}><Trash2 className="w-4 h-4" /></button></td>
    </tr>
  );
};

const Head = ({ cols }) => (
  <thead><tr className="bg-black text-white">{cols.map((h) => <th key={h} className="border border-zinc-700 px-2 py-2 font-bold text-[12px] text-center">{h}</th>)}</tr></thead>
);

// Editable reimbursement items, each saved to its date (persisted per user)
export const Reimbursements = ({ items, setItems, defaultDate }) => {
  const add = async () => {
    try {
      const { data } = await axios.post(`${API}/reimbursements`, { date: defaultDate, note: "", amount: 0 });
      setItems((p) => [...p, { ...data, amount: "" }]);
    } catch { toast.error("Could not add a reimbursement"); }
  };
  const change = (next) => setItems((p) => p.map((x) => (x.id === next.id ? next : x)));
  const save = async (next) => {
    change(next);
    try { await axios.put(`${API}/reimbursements/${next.id}`, toBody(next)); } catch { toast.error("Could not save the reimbursement"); }
  };
  const remove = async (id) => {
    try { await axios.delete(`${API}/reimbursements/${id}`); setItems((p) => p.filter((x) => x.id !== id)); } catch { toast.error("Could not remove it"); }
  };
  return (
    <div className="space-y-1.5" data-testid="reimb-section">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-zinc-700">Reimbursements</div>
        <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={add} data-testid="reimb-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add item</Button>
      </div>
      <div className="overflow-x-auto border border-zinc-400 rounded">
        <table className="w-full border-collapse text-xs" data-testid="reimb-table">
          <Head cols={["Date", "Note", "Amount", "Receipt", ""]} />
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-3 bg-white" data-testid="reimb-empty">No reimbursements — use Add item for fuel, hotels, parking, supplies…</td></tr>}
            {items.map((it, i) => <ReimbRow key={it.id} item={it} i={i} onChange={change} onSave={save} onRemove={() => remove(it.id)} />)}
          </tbody>
          {items.length > 0 && (
            <tfoot><tr className="bg-zinc-100 font-semibold"><td className="border border-zinc-400 px-2 py-1.5" colSpan={2}>Reimbursement total</td><td className="border border-zinc-400 px-2 py-1.5 text-right" data-testid="reimb-total">{usd(reimbTotal(items))}</td><td className="border border-zinc-400" colSpan={2} /></tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// Read-only list on the generated report
export const ReimbursementsResult = ({ items }) => (
  <div className="space-y-1.5" data-testid="cost-result-reimb">
    <div className="text-xs font-semibold text-zinc-700">Reimbursements</div>
    <div className="overflow-x-auto border border-zinc-400 rounded">
      <table className="w-full border-collapse text-xs">
        <Head cols={["Date", "Note", "Amount", "Receipt"]} />
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-500 py-3 bg-white">No reimbursements.</td></tr>}
          {items.map((it, i) => (
            <tr key={it.id || i} className="bg-white" data-testid={`cost-result-reimb-${i}`}>
              <td className="border border-zinc-400 px-2 py-1.5 whitespace-nowrap">{it.date}</td>
              <td className="border border-zinc-400 px-2 py-1.5">{it.note || "—"}</td>
              <td className="border border-zinc-400 px-2 py-1.5 text-right tabular-nums">{usd(it.amount)}</td>
              <td className="border border-zinc-400 px-2 py-1.5">{it.receipt_url ? <ReceiptLink item={it} testId={`cost-result-reimb-receipt-${i}`} /> : <span className="text-zinc-400">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
